// Outreach-days manager.
// Tracks the dates + hours + city each staff member (George / Racquel /
// Christian) worked outreach. These numbers feed the "Avg per outreach day"
// on the per-staff registration charts in the Stats tab.
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarDays, Plus, Trash2, X as XIcon, MapPin, Pencil, Plane, Share2, CoffeeIcon, Wrench } from "lucide-react";
import SchedulePreviewDialog from "@/pages/consult/SchedulePreviewDialog";

import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { api } from "@/lib/consult/api";
import { TimePicker15 } from "@/components/consult/TimePicker15";

const DARK_GREEN = "#2E7D5B";
const LABEL_GREEN = "#2D5A4C";
const TEXT = "#1C2B26";
const MUTED = "#6B7A73";
const BORDER = "#E5E2DC";

// Staff pills come from the shared registry — new staff added via Users
// admin appear automatically. STAFF is the fallback before the fetch lands.
const STAFF = [
  { full: "George Holness", short: "George" },
  { full: "Malachi Whyte", short: "Malachi" },
  { full: "Racquel Anthony", short: "Racquel" },
  { full: "Manitoba", short: "Manitoba" },
  { full: "Christian Marcoux", short: "Christian" },
];

// Module-level cache of loaded month entries. This panel unmounts whenever
// its tab is hidden, and without the cache the calendar flashes plain (no
// type colours) while entries refetch after every MD ↔ Calendar flip.
const ENTRY_CACHE = new Map(); // `${staff}|YYYY-M` -> entries[]
const entryCacheKey = (st, vm) => `${st}|${vm.getFullYear()}-${vm.getMonth()}`;

const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const fromYMD = (ymd) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const toMins = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};
// When "00:00" appears as an END-of-window time it means the shift runs
// through to end-of-day (midnight = 24:00). This helper normalises that
// case so validation, overlap checks, and duration math all work.
const endMins = (hhmm) => (hhmm === "00:00" ? 24 * 60 : toMins(hhmm));
const prettyTime = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  // Zero-pad the hour too so single-digit times (e.g. 03:30 PM) line up
  // vertically with double-digit ones (11:00 AM) in the entries list.
  return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
};

const labelStyle = {
  fontSize: 12, fontWeight: 700, letterSpacing: "0.24em",
  textTransform: "uppercase", color: LABEL_GREEN, margin: 0,
};
// Left inset for every editor title — puts all headings on one vertical
// line with the text inside the fields and the Month status box content.
const TITLE_INSET = 13;

export default function OutreachPanel({ canEdit = true, refreshKey = 0, lockStaff = null, canManageCities = false }) {
  const [staff, setStaff] = useState(lockStaff || STAFF[0].full);
  // Live staff list from the shared registry — new staff added via Users
  // admin appear as their own Calendar pill automatically.
  const [staffList, setStaffList] = useState(STAFF);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/staff-teams");
        if (Array.isArray(data?.staff) && data.staff.length) {
          setStaffList(data.staff.map((s) => ({ full: s.full, short: s.short })));
        }
      } catch { /* keep fallback */ }
    })();
  }, []);
  // staff preview: lock the calendar to the signed-in staff member — the
  // selector collapses to just them so other staff calendars can't be opened
  useEffect(() => { if (lockStaff && staff !== lockStaff) setStaff(lockStaff); }, [lockStaff, staff]);
  // Narrow-viewport detection: on phones <400px wide, use the compact
  // TimePicker variant so the two pickers + dash + trash button still
  // fit inline on one row without the ✕ getting clipped by the card edge.
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 399px)").matches,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 399px)");
    const on = (e) => setIsNarrow(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const [selectedDate, setSelectedDate] = useState(new Date());
  // Track which calendar month is currently visible (may differ from
  // selectedDate — e.g. user has navigated the month arrows without
  // picking a specific day). Everything the entry list, calendar markers,
  // and stats show is scoped to this month.
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [entries, setEntries] = useState(() => {
    // Seed from the module cache so a remount (tab flip) shows the last
    // loaded colours instantly instead of a plain calendar flash.
    const now = new Date();
    return ENTRY_CACHE.get(entryCacheKey(lockStaff || STAFF[0].full, now)) || [];
  });           // list for current staff + month
  const [cities, setCities] = useState([]);

  const [editKind, setEditKind] = useState("outreach"); // "outreach" | "travel"
  const [editWindows, setEditWindows] = useState([{ start_time: "09:00", end_time: "17:00", work_type: "outreach" }]);
  const [editCity, setEditCity] = useState("");
  // Day-level task (Work / Admin / Driving / …) — applies to every time
  // window of the day, chosen beside the City field.
  const [editWorkType, setEditWorkType] = useState("outreach");
  const [hoursTbd, setHoursTbd] = useState(false);
  const [saving, setSaving] = useState(false);

  // Configurable work-type list. Base 3 are always present; admins can
  // rename them and add up to a small set of extras. Stored SERVER-SIDE
  // (shared across every device/browser); localStorage is only a cache
  // that smooths the first paint while the server list loads.
  const WORK_TYPE_STORAGE_KEY = "hepcure.outreach.work_types";
  const DEFAULT_WORK_TYPES = [
    { key: "outreach", label: "Outreach" }, // counts toward hours-per-patient
    { key: "admin",    label: "Admin" },    // excluded
    { key: "driving",  label: "Driving" },  // excluded
    { key: "repair",   label: "Repair" },   // excluded
  ];
  const [workTypes, setWorkTypes] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_WORK_TYPES;
    try {
      const raw = window.localStorage.getItem(WORK_TYPE_STORAGE_KEY);
      if (!raw) return DEFAULT_WORK_TYPES;
      const parsed = JSON.parse(raw);
      // Basic shape check — fall back to defaults if corrupted.
      if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_WORK_TYPES;
      return parsed;
    } catch { return DEFAULT_WORK_TYPES; }
  });
  useEffect(() => {
    try { window.localStorage.setItem(WORK_TYPE_STORAGE_KEY, JSON.stringify(workTypes)); }
    catch (_e) { /* quota — ignore */ }
  }, [workTypes]);
  // Load the shared list from the server — this is the source of truth,
  // so custom types (e.g. "BD") appear on every device, not just the
  // browser where they were first added.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/outreach-work-types");
        if (Array.isArray(data?.types) && data.types.length) setWorkTypes(data.types);
      } catch { /* keep the localStorage/defaults fallback */ }
    })();
  }, []);

  // Month-level completion status per (staff, month). Controls whether
  // this staff member's hours worked appears on the Stats tab.
  const [monthCompleted, setMonthCompleted] = useState(false);
  const [monthStatusSaving, setMonthStatusSaving] = useState(false);

  const [manageCitiesOpen, setManageCitiesOpen] = useState(false);
  const [newCity, setNewCity] = useState("");
  const [renameCityFrom, setRenameCityFrom] = useState("");
  const [renameCityTo, setRenameCityTo] = useState("");

  const [shareOpen, setShareOpen] = useState(false);

  const selectedYMD = toYMD(selectedDate);

  // ---------- loaders ----------
  const loadEntries = useCallback(async () => {
    try {
      // Include a ~7-day buffer on each side so the calendar's
      // previous-/next-month spillover cells (e.g. Aug 30-31 shown in the
      // September grid) still get their real type colour.
      const y = viewMonth.getFullYear();
      const m = viewMonth.getMonth();
      const buf = 7;
      const fromDate = new Date(y, m, 1);
      fromDate.setDate(fromDate.getDate() - buf);
      const toDate = new Date(y, m + 1, 0);
      toDate.setDate(toDate.getDate() + buf);
      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const { data } = await api.get("/outreach", {
        params: { staff, from: fmt(fromDate), to: fmt(toDate) },
      });
      ENTRY_CACHE.set(entryCacheKey(staff, viewMonth), data || []);
      setEntries(data || []);
    } catch { /* handled by 401 interceptor */ }
  }, [staff, viewMonth]);

  const loadCities = useCallback(async () => {
    try {
      const { data } = await api.get("/outreach-cities");
      setCities(data?.cities || []);
    } catch { /* ignore */ }
  }, []);

  const monthKey = useMemo(
    () => `${viewMonth.getFullYear()}-${String(viewMonth.getMonth() + 1).padStart(2, "0")}`,
    [viewMonth],
  );
  const loadMonthStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/outreach-status", {
        params: { staff, month: monthKey },
      });
      setMonthCompleted(Boolean(data?.completed));
    } catch { /* ignore */ }
  }, [staff, monthKey]);

  useEffect(() => { loadEntries(); }, [loadEntries]);
  useEffect(() => { loadCities(); }, [loadCities]);
  useEffect(() => { loadMonthStatus(); }, [loadMonthStatus]);

  // Refresh handshake: when the Dashboard toolbar Refresh button bumps
  // `refreshKey`, re-run every loader in this panel.
  useEffect(() => {
    if (refreshKey === 0) return;
    loadEntries();
    loadCities();
    loadMonthStatus();
  }, [refreshKey, loadEntries, loadCities, loadMonthStatus]);

  // Sync editor to current staff/day
  const currentEntry = useMemo(
    () => entries.find((e) => e.date === selectedYMD),
    [entries, selectedYMD],
  );
  // Sync editor to current staff/day. A plain `[currentEntry, ...]` dep
  // re-runs on every background entries refresh and clobbers an in-progress
  // edit (e.g. tapping a Type the moment a day opens), so only sync when the
  // day/staff actually changes — or when the entry for the open day arrives
  // after a blank editor was already shown.
  const editorSyncRef = useRef({ staff: null, ymd: null, hadEntry: false });
  // Set true by any user edit in the day editor; while dirty, background
  // entry refreshes must never reset the editor (only a day/staff switch may).
  const editorDirtyRef = useRef(false);
  useEffect(() => {
    const prev = editorSyncRef.current;
    // Only a genuine day/staff switch forces a re-sync. The staff name
    // materializing after mount ("" → scoped name once perms resolve) is NOT
    // a switch, so an early user tap isn't clobbered by it.
    const switched =
      prev.ymd !== selectedYMD ||
      (prev.staff && staff && prev.staff !== staff);
    if (!switched) {
      if (editorDirtyRef.current) return;
      if (prev.hadEntry || !currentEntry) return;
    }
    editorSyncRef.current = { staff, ymd: selectedYMD, hadEntry: !!currentEntry };
    editorDirtyRef.current = false;
    if (currentEntry) {
      const k = currentEntry.kind;
      setEditKind(k === "travel" ? "travel" : k === "off" ? "off" : k === "repair" ? "repair" : k === "admin" ? "admin" : "outreach");
      // Windows loaded from a saved day are tagged `_saved` — the top Task
      // dropdown only retags fresh (not-yet-saved) windows, never these.
      const ws = (currentEntry.windows || []).map((w) => ({ ...w, _saved: true }));
      setHoursTbd(ws.length === 0);
      setEditWindows(ws.length ? ws : [{ start_time: "09:00", end_time: "17:00", work_type: "outreach" }]);
      setEditWorkType(ws[0]?.work_type || "outreach");
      setEditCity(currentEntry.city || "");
    } else {
      setEditKind("outreach");
      setHoursTbd(false);
      setEditWindows([{ start_time: "09:00", end_time: "17:00", work_type: "outreach" }]);
      setEditWorkType("outreach");
      setEditCity("");
    }
  }, [currentEntry, selectedYMD, staff]);

  const toggleMonthStatus = async (next) => {
    setMonthStatusSaving(true);
    try {
      await api.post("/outreach-status", {
        staff, month: monthKey, completed: next,
      });
      setMonthCompleted(next);
      toast.success(next ? "Month marked complete." : "Month marked incomplete.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update status.");
    } finally {
      setMonthStatusSaving(false);
    }
  };

  // Calendar markers — colours ALL entries regardless of the currently
  // viewed month so days that spill over from the prior/next month
  // (e.g. Aug 30/31 shown at the top of the Sept grid) keep their
  // real category colour instead of appearing empty or grey.
  const outreachDates = useMemo(
    () => entries.filter((e) => e.kind === "outreach" || !e.kind).map((e) => fromYMD(e.date)),
    [entries],
  );
  const travelDates = useMemo(
    () => entries.filter((e) => e.kind === "travel").map((e) => fromYMD(e.date)),
    [entries],
  );
  const offDates = useMemo(
    () => entries.filter((e) => e.kind === "off").map((e) => fromYMD(e.date)),
    [entries],
  );
  const repairDates = useMemo(
    () => entries.filter((e) => e.kind === "repair").map((e) => fromYMD(e.date)),
    [entries],
  );
  const adminDates = useMemo(
    () => entries.filter((e) => e.kind === "admin").map((e) => fromYMD(e.date)),
    [entries],
  );
  const modifiers = { hasOutreach: outreachDates, hasTravel: travelDates, hasOff: offDates, hasRepair: repairDates, hasAdmin: adminDates };
  const modifiersClassNames = {
    hasOutreach:
      "!bg-[#2E7D5B] !text-white !font-bold hover:!bg-[#2E7D5B]/90 focus:!bg-[#2E7D5B] aria-selected:!bg-[#2E7D5B] aria-selected:!text-white",
    hasTravel:
      "!bg-[#1F4E79] !text-white !font-bold hover:!bg-[#1F4E79]/90 focus:!bg-[#1F4E79] aria-selected:!bg-[#1F4E79] aria-selected:!text-white",
    hasOff:
      "!bg-[#B4351B] !text-white !font-bold hover:!bg-[#B4351B]/90 focus:!bg-[#B4351B] aria-selected:!bg-[#B4351B] aria-selected:!text-white",
    hasRepair:
      "!bg-[#C77D26] !text-white !font-bold hover:!bg-[#C77D26]/90 focus:!bg-[#C77D26] aria-selected:!bg-[#C77D26] aria-selected:!text-white",
    hasAdmin:
      "!bg-[#1F2937] !text-white !font-bold hover:!bg-[#1F2937]/90 focus:!bg-[#1F2937] aria-selected:!bg-[#1F2937] aria-selected:!text-white",
  };
  // We *also* restrict to dates within the currently-visible month so that
  // spillover days from prev/next month always render blank, even during
  // a stale-fetch race after month navigation.
  const inViewMonth = (ymd) => {
    const [y, m] = ymd.split("-").map(Number);
    return y === viewMonth.getFullYear() && m === viewMonth.getMonth() + 1;
  };

  const setWindow = (idx, patch) => {
    editorDirtyRef.current = true;
    setEditWindows((ws) => ws.map((w, i) => (i === idx ? { ...w, ...patch } : w)));
  };
  // Type buttons set the calendar colour AND default the Task to the matching
  // option — Work → Outreach, Travel → Driving, Repair → Repair, Admin →
  // Admin. Changing the Task retags only FRESH windows (added this session,
  // not yet saved); slots loaded from a saved day are never touched.
  const KIND_DEFAULT_TASK = { outreach: "outreach", travel: "driving", repair: "repair", admin: "admin" };
  const applyTaskDefault = (task) => {
    editorDirtyRef.current = true;
    setEditWorkType(task);
    // Retag ONLY the most recently added unsaved window — that's the slot the
    // user is currently setting up. Saved slots and earlier fresh slots keep
    // the task they were given.
    setEditWindows((ws) => {
      let lastFresh = -1;
      ws.forEach((w, i) => { if (!w._saved) lastFresh = i; });
      if (lastFresh === -1) return ws;
      return ws.map((w, i) => (i === lastFresh ? { ...w, work_type: task } : w));
    });
  };
  const pickKind = (k) => {
    editorDirtyRef.current = true;
    setEditKind(k);
    if (k !== "off") applyTaskDefault(KIND_DEFAULT_TASK[k] || "outreach");
  };
  const addWindow = () => {
    editorDirtyRef.current = true;
    const last = editWindows[editWindows.length - 1];
    const lastEnd = last ? toMins(last.end_time) : 9 * 60;
    const s = Math.min(lastEnd + 60, 22 * 60);
    const e = Math.min(s + 60, 23 * 60 + 45);
    const fmt = (t) => `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
    setEditWindows((ws) => [...ws, { start_time: fmt(s), end_time: fmt(e), work_type: editWorkType }]);
  };
  const removeWindow = (idx) => {
    editorDirtyRef.current = true;
    setEditWindows((ws) => ws.length > 1 ? ws.filter((_, i) => i !== idx) : ws);
  };

  const save = async () => {
    // Off days have no hours — skip window validation. Repair days may
    // have hours (e.g. mechanic worked 9 AM – 4 PM on the vehicle) so
    // they are validated the same as outreach/travel unless the user
    // ticks "Hours not worked" (hoursTbd).
    if (!hoursTbd && editKind !== "off") {
      for (const w of editWindows) {
        if (!w.start_time || !w.end_time || endMins(w.end_time) <= toMins(w.start_time)) {
          toast.error("Each window must have an end later than its start.");
          return;
        }
      }
      const sorted = [...editWindows].sort((a, b) => toMins(a.start_time) - toMins(b.start_time));
      for (let i = 1; i < sorted.length; i++) {
        if (toMins(sorted[i].start_time) < endMins(sorted[i - 1].end_time)) {
          toast.error("Outreach windows overlap. Adjust the times.");
          return;
        }
      }
    }
    setSaving(true);
    try {
      // Off days never have work hours. Repair days may have hours, and
      // if the user ticked "Hours not worked" (hoursTbd) we save [].
      const effectiveWindows =
        editKind === "off" ? [] : (hoursTbd ? [] : editWindows.map((w) => ({
          start_time: w.start_time,
          end_time: w.end_time,
          work_type: w.work_type || editWorkType,
        })));
      await api.post("/outreach", {
        staff,
        date: selectedYMD,
        kind: editKind,
        city: editKind === "off" ? "" : editCity,
        windows: effectiveWindows,
      });
      toast.success(
        editKind === "travel" ? "Travel day saved."
        : editKind === "off"  ? "Off day saved."
        : editKind === "repair" ? "Repair day saved."
        : editKind === "admin" ? "Admin day saved."
        : "Outreach day saved."
      );
      // Every window now matches what was saved — tag them all so a later
      // Task change doesn't retag them (only newly added slots follow it).
      setEditWindows((ws) => ws.map((w) => ({ ...w, _saved: true })));
      await loadEntries();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!currentEntry) return;
    if (!window.confirm(`Remove ${staff.split(" ")[0]}'s outreach on ${selectedYMD}?`)) return;
    try {
      await api.delete(`/outreach/${encodeURIComponent(staff)}/${selectedYMD}`);
      toast.success("Outreach day removed.");
      await loadEntries();
    } catch {
      toast.error("Failed to remove outreach day.");
    }
  };

  const addCity = async () => {
    const name = newCity.trim();
    if (!name) return;
    try {
      await api.post("/outreach-cities", { name });
      setNewCity("");
      await loadCities();
      setEditCity(name);
      toast.success(`"${name}" added.`);
    } catch {
      toast.error("Failed to add city.");
    }
  };

  const removeCity = async (name) => {
    if (!window.confirm(`Remove "${name}" from the city list? Existing outreach entries will keep their city label until you re-save them.`)) return;
    try {
      await api.delete(`/outreach-cities/${encodeURIComponent(name)}`);
      await loadCities();
      if (editCity === name) setEditCity("");
      toast.success(`"${name}" removed.`);
    } catch {
      toast.error("Failed to remove city.");
    }
  };

  const openRenameCity = (name) => {
    // Inline edit mode inside the Manage cities dialog.
    setRenameCityFrom(name);
    setRenameCityTo(name);
  };
  const doRenameCity = async () => {
    const from = renameCityFrom.trim();
    const to = renameCityTo.trim();
    if (!to || to === from) { setRenameCityFrom(""); return; }
    try {
      const { data } = await api.patch(`/outreach-cities/${encodeURIComponent(from)}`, { new_name: to });
      setRenameCityFrom("");
      await Promise.all([loadCities(), loadEntries()]);
      if (editCity === from) setEditCity(to);
      toast.success(`Renamed to "${to}". Updated ${data?.renamed_entries || 0} entr${(data?.renamed_entries || 0) === 1 ? "y" : "ies"}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to rename city.");
    }
  };

  // Sub-tab list
  return (
    <div
      className="consult-grid"
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(320px, 440px) 1fr",
        gap: 24,
        alignItems: "start",
      }}
    >
      {/* Left column — staff switcher + calendar */}
      <div className="outreach-card" style={{
        background: "white", borderRadius: 14, border: `1px solid ${BORDER}`,
        padding: 20,
        minWidth: 0,
      }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: lockStaff ? 20 : 12, gap: 8, flexWrap: "nowrap",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <p style={{ ...labelStyle, margin: 0 }}>Staff member</p>
            {canEdit && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShareOpen(true)}
                data-testid="outreach-share-btn"
                aria-label="Share schedule"
                title="Share schedule"
                style={{ height: 32, width: 32, padding: 0 }}
              >
                <Share2 style={{ width: 13, height: 13 }} strokeWidth={2} />
              </Button>
            )}
          </div>
          {/* Staff logins: their name pill sits flush-right here — the exact
              same spot as on the MD tab, so it never moves when flipping. */}
          {lockStaff && (
            <span
              data-testid={`outreach-staff-${lockStaff.split(" ")[0].toLowerCase()}`}
              style={{
                padding: "8px 14px", borderRadius: 8,
                border: `1.5px solid ${DARK_GREEN}`, background: DARK_GREEN,
                color: "white", fontSize: 11, fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {lockStaff.split(" ")[0]}
            </span>
          )}
        </div>
        {/* Admin: staff pills in a single horizontally scrollable strip
            (scroll left/right) — never wraps to a second row. Staff logins
            don't get this row at all (their pill is on the label line). */}
        {!lockStaff && (
        <div
          className="hide-scrollbar"
          data-testid="outreach-staff-strip"
          style={{
            display: "flex", gap: 6, marginBottom: 20,
            overflowX: "auto", flexWrap: "nowrap",
            WebkitOverflowScrolling: "touch",
            paddingBottom: 2,
          }}
        >
          {staffList.map((s) => (
            <button
              key={s.full}
              type="button"
              onClick={() => setStaff(s.full)}
              data-testid={`outreach-staff-${s.short.toLowerCase()}`}
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                border: `1.5px solid ${staff === s.full ? DARK_GREEN : BORDER}`,
                background: staff === s.full ? DARK_GREEN : "white",
                color: staff === s.full ? "white" : MUTED,
                fontSize: 11, fontWeight: 600, cursor: "pointer",
                fontFamily: "inherit",
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              {s.short}
            </button>
          ))}
        </div>
        )}

        <p style={{ ...labelStyle, marginBottom: 8, textAlign: "center" }}>Pick a date</p>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Calendar
            mode="single"
            month={viewMonth}
            onMonthChange={setViewMonth}
            selected={selectedDate}
            onSelect={(d) => {
              if (!d) return;
              setSelectedDate(d);
              // If the user tapped a date that's on the previous / next
              // month sliver, snap the visible month to that date.
              if (d.getMonth() !== viewMonth.getMonth() || d.getFullYear() !== viewMonth.getFullYear()) {
                setViewMonth(new Date(d.getFullYear(), d.getMonth(), 1));
              }
            }}
            modifiers={modifiers}
            modifiersClassNames={modifiersClassNames}
            // Selected day is always a solid dark-green fill so the picked
            // date never "goes white"; days with entries keep their type
            // colour (aria-selected in the modifiers below wins on those).
            classNames={{
              day_selected: "!bg-[#0F3B2E] !text-white !font-semibold ring-2 ring-inset ring-[#0F172A]/40",
              day_today: "ring-1 ring-inset ring-[#0F172A]/25",
              day_outside: "day-outside",
            }}
            className="p-0"
          />
        </div>
        <div style={{
          fontSize: 11.5, color: MUTED, marginTop: 10,
          display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center",
        }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: DARK_GREEN }} />
            Work
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: "#1F4E79" }} />
            Travel
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: "#B4351B" }} />
            Off
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: "#C77D26" }} />
            Repair
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: "#1F2937" }} />
            Admin
          </span>
        </div>
      </div>

      {/* Right column — editor + list */}
      <div className="outreach-card" style={{
        background: "white", borderRadius: 14, border: `1px solid ${BORDER}`,
        padding: 20,
        minWidth: 0,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
          <div>
            <p style={{ ...labelStyle, marginBottom: 4, marginLeft: TITLE_INSET }}>
              {format(fromYMD(selectedYMD), "EEEE, MMM d, yyyy")}
            </p>
            <p style={{ fontSize: 12, color: MUTED, margin: 0, marginLeft: TITLE_INSET }}>
              {currentEntry
                ? (editKind === "travel" ? "Editing existing travel day"
                  : editKind === "off"    ? "Editing existing off day"
                  : editKind === "repair" ? "Editing existing repair day"
                  : editKind === "admin"  ? "Editing existing admin day"
                  : "Editing existing outreach day")
                : "New entry"}
            </p>
          </div>
        </div>

        {canEdit ? (
        <>
        {/* Kind toggle — Work / Travel / Off / Repair / Admin.
            Horizontally scrollable on narrow screens so labels never wrap
            or truncate — swipe/scroll to reach the last option. */}
        <div style={{ marginBottom: 18 }}>
          <p style={{ ...labelStyle, marginBottom: 8, marginLeft: TITLE_INSET }}>Type</p>
          <div
            className="outreach-kind-row hide-scrollbar"
            style={{
              display: "flex", flexWrap: "nowrap", gap: 6,
              overflowX: "auto", WebkitOverflowScrolling: "touch",
            }}
          >
            <button
              type="button"
              onClick={() => pickKind("outreach")}
              data-testid="outreach-kind-outreach"
              style={{
                flex: "1 1 auto", minWidth: 92,
                padding: "10px 10px",
                borderRadius: 8,
                border: `1.5px solid ${editKind === "outreach" ? DARK_GREEN : BORDER}`,
                background: editKind === "outreach" ? DARK_GREEN : "white",
                color: editKind === "outreach" ? "white" : MUTED,
                fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                fontFamily: "inherit",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                whiteSpace: "nowrap",
              }}
            >
              <MapPin style={{ width: 13, height: 13, flexShrink: 0 }} strokeWidth={2.5} />
              <span>Work</span>
            </button>
            <button
              type="button"
              onClick={() => pickKind("travel")}
              data-testid="outreach-kind-travel"
              style={{
                flex: "1 1 auto", minWidth: 92,
                padding: "10px 10px",
                borderRadius: 8,
                border: `1.5px solid ${editKind === "travel" ? "#1F4E79" : BORDER}`,
                background: editKind === "travel" ? "#1F4E79" : "white",
                color: editKind === "travel" ? "white" : MUTED,
                fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                fontFamily: "inherit",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                whiteSpace: "nowrap",
              }}
            >
              <Plane style={{ width: 13, height: 13, flexShrink: 0 }} strokeWidth={2.5} />
              <span>Travel</span>
            </button>
            <button
              type="button"
              onClick={() => pickKind("repair")}
              data-testid="outreach-kind-repair"
              style={{
                flex: "1 1 auto", minWidth: 92,
                padding: "10px 10px",
                borderRadius: 8,
                border: `1.5px solid ${editKind === "repair" ? "#C77D26" : BORDER}`,
                background: editKind === "repair" ? "#C77D26" : "white",
                color: editKind === "repair" ? "white" : MUTED,
                fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                fontFamily: "inherit",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                whiteSpace: "nowrap",
              }}
            >
              <Wrench style={{ width: 13, height: 13, flexShrink: 0 }} strokeWidth={2.5} />
              <span>Repair</span>
            </button>
            <button
              type="button"
              onClick={() => pickKind("admin")}
              data-testid="outreach-kind-admin"
              style={{
                flex: "1 1 auto", minWidth: 92,
                padding: "10px 10px",
                borderRadius: 8,
                border: `1.5px solid ${editKind === "admin" ? "#1F2937" : BORDER}`,
                background: editKind === "admin" ? "#1F2937" : "white",
                color: editKind === "admin" ? "white" : MUTED,
                fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                fontFamily: "inherit",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                whiteSpace: "nowrap",
              }}
            >
              <Wrench style={{ width: 13, height: 13, flexShrink: 0 }} strokeWidth={2.5} />
              <span>Admin</span>
            </button>
            <button
              type="button"
              onClick={() => pickKind("off")}
              data-testid="outreach-kind-off"
              style={{
                flex: "1 1 auto", minWidth: 82,
                padding: "10px 10px",
                borderRadius: 8,
                border: `1.5px solid ${editKind === "off" ? "#B4351B" : BORDER}`,
                background: editKind === "off" ? "#B4351B" : "white",
                color: editKind === "off" ? "white" : MUTED,
                fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                fontFamily: "inherit",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                whiteSpace: "nowrap",
              }}
            >
              <CoffeeIcon style={{ width: 13, height: 13, flexShrink: 0 }} strokeWidth={2.5} />
              <span>Off</span>
            </button>
          </div>
          <p style={{ fontSize: 11, color: MUTED, marginTop: 6, marginBottom: 0, marginLeft: TITLE_INSET }}>
            Type sets the calendar colour — every type uses the same form. Only Off days skip hours; Repair and Admin stay separate on Stats, Travel counts as working days.          </p>
        </div>

        {/* City + Task — the same work form for Work, Travel, Repair and
            Admin days; the type button only sets the calendar colour.
            Only Off days skip this. */}
        {editKind !== "off" && (
          <div style={{ marginBottom: 18 }}>
            {/* City + Task share one line; city admin lives behind Manage. */}
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.25fr) minmax(0, 1fr)", gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <p style={{ ...labelStyle, marginBottom: 8, marginLeft: TITLE_INSET, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <MapPin style={{ width: 12, height: 12 }} strokeWidth={2.5} />
                    City
                  </span>
                  {canManageCities && (
                    <button
                      type="button"
                      onClick={() => setManageCitiesOpen(true)}
                      data-testid="outreach-city-manage-btn"
                      style={{
                        background: "none", border: "none", padding: 0, cursor: "pointer",
                        color: LABEL_GREEN, fontSize: 10, fontWeight: 700,
                        letterSpacing: "0.06em", textTransform: "uppercase",
                        fontFamily: "inherit", whiteSpace: "nowrap",
                      }}
                    >
                      Manage
                    </button>
                  )}
                </p>
                <Select value={editCity || "__none__"} onValueChange={(v) => { editorDirtyRef.current = true; setEditCity(v === "__none__" ? "" : v); }}>
                  <SelectTrigger data-testid="outreach-city-select" className="w-full">
                    <SelectValue placeholder="Please select" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 overscroll-contain">
                    <SelectItem value="__none__">Please select</SelectItem>
                    {cities.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div style={{ minWidth: 0 }}>
                <p style={{ ...labelStyle, marginBottom: 8, marginLeft: TITLE_INSET }}>Task</p>
                <Select value={editWorkType} onValueChange={(v) => {
                  // The Task retags only freshly added (not yet saved) slots —
                  // slots loaded from a saved day keep their own task. Mixed
                  // flow: add a slot, pick its task, save, repeat.
                  applyTaskDefault(v);
                }}>
                  <SelectTrigger data-testid="outreach-day-task" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {workTypes.map((wt) => (
                      <SelectItem key={wt.key} value={wt.key}>{wt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* Windows — hidden for off days (rest days by definition have no work hours) */}
        {editKind !== "off" && (
        <div style={{ marginBottom: 18 }}>
          {hoursTbd ? (
            <>
              <div style={{
                display: "grid", gridTemplateColumns: "minmax(0, 1.25fr) minmax(0, 1fr)",
                gap: 10, alignItems: "center", marginBottom: 10,
              }}>
                <p style={{ ...labelStyle, margin: 0, marginLeft: TITLE_INSET }}>Hours worked</p>
                <label style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  fontSize: 11.5, color: LABEL_GREEN, fontWeight: 600, cursor: "pointer",
                  whiteSpace: "nowrap", marginLeft: TITLE_INSET,
                }}>
                  <input
                    type="checkbox"
                    checked={hoursTbd}
                    onChange={(e) => { editorDirtyRef.current = true; setHoursTbd(e.target.checked); }}
                    data-testid="outreach-hours-tbd"
                    style={{ cursor: "pointer", accentColor: "#2E7D5B" }}
                  />
                  {editKind === "repair" ? "Hours not worked" : "Hours not yet set"}
                </label>
              </div>
              <div style={{
                padding: "12px 14px",
                borderRadius: 10,
                border: `1.5px dashed ${BORDER}`,
                background: "#F9F8F6",
                color: MUTED,
                fontSize: 13,
                textAlign: "center",
                fontStyle: "italic",
              }}>
                Hours will be added later. This date is scheduled and will show
                on the calendar; add hours when known.
              </div>
            </>
          ) : (
            <>
              {/* "Hours worked" with the "Hours not yet set" checkbox beside
                  it — same two-column grid as City/Task above, so the
                  checkbox starts exactly at the Task column's left edge. */}
              <div style={{
                display: "grid", gridTemplateColumns: "minmax(0, 1.25fr) minmax(0, 1fr)",
                gap: 10, alignItems: "center", marginBottom: 8,
              }}>
                <p style={{ ...labelStyle, margin: 0, marginLeft: TITLE_INSET }}>Hours worked</p>
                <label style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  fontSize: 11.5, color: LABEL_GREEN, fontWeight: 600, cursor: "pointer",
                  whiteSpace: "nowrap", marginLeft: TITLE_INSET,
                }}>
                  <input
                    type="checkbox"
                    checked={hoursTbd}
                    onChange={(e) => { editorDirtyRef.current = true; setHoursTbd(e.target.checked); }}
                    data-testid="outreach-hours-tbd"
                    style={{ cursor: "pointer", accentColor: "#2E7D5B" }}
                  />
                  {editKind === "repair" ? "Hours not worked" : "Hours not yet set"}
                </label>
              </div>
              {editWindows.map((w, idx) => (
                <div key={idx} style={{
                  display: "flex", alignItems: "center", gap: 4,
                  marginBottom: 8, minWidth: 0, flexWrap: "wrap",
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    flexShrink: 0,
                  }}>
                    <TimePicker15
                      value={w.start_time}
                      onChange={(v) => setWindow(idx, { start_time: v })}
                      testIdPrefix={`outreach-window-${idx}-start`}
                      compact={isNarrow}
                    />
                    <span
                      aria-hidden
                      data-testid={`outreach-window-${idx}-dash`}
                      style={{
                        color: MUTED,
                        fontWeight: 700,
                        flexShrink: 0,
                        fontSize: 16,
                        lineHeight: 1,
                      }}
                    >
                      –
                    </span>
                    <TimePicker15
                      value={w.end_time}
                      onChange={(v) => setWindow(idx, { end_time: v })}
                      testIdPrefix={`outreach-window-${idx}-end`}
                      compact={isNarrow}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeWindow(idx)}
                    disabled={editWindows.length <= 1}
                    data-testid={`outreach-window-${idx}-remove`}
                    style={{
                      background: "transparent",
                      border: `1px solid ${BORDER}`,
                      borderRadius: 6, padding: 2,
                      cursor: editWindows.length > 1 ? "pointer" : "not-allowed",
                      opacity: editWindows.length > 1 ? 1 : 0.4,
                      color: MUTED,
                      display: "flex", alignItems: "center", flexShrink: 0,
                    }}
                  >
                    <XIcon style={{ width: 14, height: 14 }} />
                  </button>
                </div>
              ))}
              {/* "Add another time window" sits directly below the last time slot. */}
              <div style={{ display: "flex", marginBottom: 8 }}>
                <button
                  type="button"
                  onClick={addWindow}
                  data-testid="outreach-window-add"
                  style={{
                    padding: "6px 12px",
                    borderRadius: 8,
                    border: `1.5px dashed ${BORDER}`,
                    background: "transparent",
                    color: LABEL_GREEN,
                    fontSize: 12.5, fontWeight: 600,
                    cursor: "pointer",
                    display: "inline-flex", alignItems: "center", gap: 6,
                    fontFamily: "inherit",
                  }}
                >
                  <Plus style={{ width: 14, height: 14 }} /> Add another time window
                </button>
              </div>
            </>
          )}
        </div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <Button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="outreach-save"
            style={{ background: DARK_GREEN, color: "white", flex: 1 }}
          >
            {saving ? "Saving…" : currentEntry
              ? (editKind === "travel" ? "Update travel day"
                : editKind === "off"    ? "Update off day"
                : editKind === "repair" ? "Update repair day"
                : "Update outreach day")
              : (editKind === "travel" ? "Save travel day"
                : editKind === "off"    ? "Save off day"
                : editKind === "repair" ? "Save repair day"
                : "Save outreach day")}
          </Button>
          {currentEntry && (
            <Button
              type="button"
              variant="outline"
              onClick={remove}
              data-testid="outreach-remove"
            >
              <Trash2 style={{ width: 14, height: 14 }} />
            </Button>
          )}
        </div>
        </>
        ) : (
          <ReadOnlyEntryCard entry={currentEntry} />
        )}

        {/* List of entries for this staff */}
        <div style={{ marginTop: 24 }}>
          {(() => {
            // Off days appear on the calendar but not in this working-days
            // summary — this list is meant to reflect actual working /
            // travelling activity only.
            // The buffer fetch pulls previous/next-month spillover days
            // (so the calendar colours correctly). Filter those out of
            // the visible list so we only show days that actually fall
            // inside the currently viewed month.
            const listEntries = entries
              .filter((e) => (e.kind || "outreach") !== "off")
              .filter((e) => inViewMonth(e.date));
            const entryHoursMap = new Map();
            listEntries.forEach((e) => {
              const total = (e.windows || []).reduce((s, w) => {
                const [sh, sm] = (w.start_time || "0:0").split(":").map(Number);
                let [eh, em] = (w.end_time   || "0:0").split(":").map(Number);
                // "00:00" as an end value means end-of-day (24:00).
                if (eh === 0 && em === 0 && (w.end_time || "") === "00:00") eh = 24;
                return s + Math.max(0, (eh * 60 + em) - (sh * 60 + sm));
              }, 0);
              entryHoursMap.set(e.date, total / 60);
            });
            const monthTotalHours = Array.from(entryHoursMap.values()).reduce((s, h) => s + h, 0);
            const monthTotalLabel = monthTotalHours === Math.floor(monthTotalHours)
              ? `${monthTotalHours} h` : `${monthTotalHours.toFixed(2)} h`;
            const fmtRowHrs = (h) => {
              if (!h) return null;
              // Always render with 1 decimal so single-digit + fractional
              // totals (e.g. "8.0 h", "10.5 h") occupy the same visual width
              // and line up cleanly in the right-hand column across rows.
              return `${h.toFixed(1)} h`;
            };
            return (
              <>
                {/* Month completion toggle — one gate that controls whether
                    this staff's hours worked appears on the Stats tab. */}
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 12, flexWrap: "wrap",
                  padding: "10px 12px", marginBottom: 12,
                  background: "#F4F3EF", border: `1px solid ${BORDER}`, borderRadius: 10,
                }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ ...labelStyle, margin: 0, marginBottom: 2 }}>
                      Month status
                    </p>
                    <p style={{ fontSize: 12, color: MUTED, margin: 0 }}>
                      Total hours entered: <b style={{ color: TEXT }}>{monthTotalLabel}</b>
                      {" · "}
                      Stats show {monthCompleted ? "these hours" : <em>“—”</em>}
                    </p>
                  </div>
                  {canEdit && (
                    <div
                      role="group"
                      aria-label="Month completion status"
                      style={{
                        display: "inline-flex", borderRadius: 8, overflow: "hidden",
                        border: `1px solid ${BORDER}`, background: "white",
                        opacity: monthStatusSaving ? 0.6 : 1,
                      }}
                    >
                      <button
                        type="button"
                        disabled={monthStatusSaving}
                        onClick={() => toggleMonthStatus(false)}
                        data-testid="outreach-month-incomplete"
                        style={{
                          padding: "6px 12px", fontSize: 12, fontWeight: 600,
                          border: "none",
                          cursor: monthStatusSaving ? "wait" : "pointer",
                          background: !monthCompleted ? "#B4351B" : "white",
                          color: !monthCompleted ? "white" : MUTED,
                          fontFamily: "inherit",
                        }}
                      >
                        Incomplete
                      </button>
                      <button
                        type="button"
                        disabled={monthStatusSaving}
                        onClick={() => toggleMonthStatus(true)}
                        data-testid="outreach-month-complete"
                        style={{
                          padding: "6px 12px", fontSize: 12, fontWeight: 600,
                          border: "none",
                          cursor: monthStatusSaving ? "wait" : "pointer",
                          borderLeft: `1px solid ${BORDER}`,
                          background: monthCompleted ? DARK_GREEN : "white",
                          color: monthCompleted ? "white" : MUTED,
                          fontFamily: "inherit",
                        }}
                      >
                        Complete
                      </button>
                    </div>
                  )}
                </div>

                <p style={{ ...labelStyle, marginBottom: 10, marginLeft: 13 }}>
                  {staff.split(" ")[0]}&rsquo;s days ({listEntries.length})
                </p>
                {listEntries.length === 0 ? (
                  <p style={{ fontSize: 13, color: MUTED, marginLeft: 13 }}>No outreach or travel days logged this month.</p>
                ) : (
                  <div style={{ display: "grid", gap: 8 }}>
                    {listEntries
                      .slice()
                      .sort((a, b) => a.date.localeCompare(b.date))
                      .map((e) => {
                        const rowHrs = fmtRowHrs(entryHoursMap.get(e.date));
                        return (
                  <button
                    key={e.date}
                    type="button"
                    onClick={() => setSelectedDate(fromYMD(e.date))}
                    data-testid={`outreach-entry-${e.date}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: `1px solid ${selectedYMD === e.date ? DARK_GREEN : BORDER}`,
                      background: selectedYMD === e.date ? "#F4F3EF" : "white",
                      cursor: "pointer",
                      fontFamily: "inherit",
                      textAlign: "left",
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: TEXT }}>
                        {format(fromYMD(e.date), "EEE, MMM d, yyyy")}
                      </div>
                      {/* Mobile-only meta line: just the city now. Total
                          hours live in the right column next to the
                          calendar icon on every viewport, so we don't
                          repeat them here. */}
                      {(e.kind !== "travel" && e.kind !== "off" && e.kind !== "repair" && e.city) && (
                        <div className="outreach-meta-mobile" style={{ fontSize: 12, color: MUTED, marginTop: 2, fontWeight: 600 }}>
                          <span>{e.city}</span>
                        </div>
                      )}
                      {/* Time windows. On mobile each window sits on its
                          own line for readability with its category name
                          inline (Work is omitted per user request). On
                          desktop they join with " · " on a single row —
                          categories on desktop live in the right column
                          in place of the day-kind badge. */}
                      <div className="outreach-times-block" style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                        {(e.windows && e.windows.length) ? (
                          <>
                            <span className="outreach-times-desktop">
                              {e.windows.map((w) => `${prettyTime(w.start_time)} – ${prettyTime(w.end_time)}`).join(" · ")}
                            </span>
                            <div className="outreach-times-mobile-list">
                              {e.windows.map((w, i) => {
                                const wt = (w.work_type || "outreach").toLowerCase();
                                const WT_LABEL_M = { outreach: "Work", admin: "Admin", driving: "Driving", repair: "Repair", other: "Other" };
                                const WT_COLOR_M = { outreach: DARK_GREEN, admin: "#8A5A00", driving: "#1F4E79", repair: "#C77D26", other: "#6B7A73" };
                                const WT_ICON_M  = { outreach: MapPin,     admin: Wrench,     driving: Plane,     repair: Wrench,   other: MapPin };
                                // Show the label from custom types if user
                                // renamed the built-ins in localStorage.
                                const custom = workTypes.find((t) => t.key === wt);
                                const label = custom?.label || WT_LABEL_M[wt] || wt;
                                const color = WT_COLOR_M[wt] || "#6B7A73";
                                const Icon = WT_ICON_M[wt] || MapPin;
                                // Every working kind shows each slot's task so
                                // Travel / Repair / Admin days get the same
                                // per-slot hours breakdown as Work days.
                                const showLabel = e.kind !== "off";
                                return (
                                  <div key={i} style={{ display: "flex", flexWrap: "nowrap", alignItems: "center", gap: 6, minWidth: 0 }}>
                                    <span style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                                      {prettyTime(w.start_time)} – {prettyTime(w.end_time)}
                                    </span>
                                    {showLabel && (
                                      <span
                                        className="outreach-cat-inline"
                                        data-testid={`outreach-row-slot-cat-${wt}`}
                                        style={{
                                          display: "inline-flex", alignItems: "center", gap: 3,
                                          padding: "1px 5px", borderRadius: 999,
                                          background: `${color}24`, color,
                                          fontSize: 9, fontWeight: 700, letterSpacing: "0.03em",
                                          textTransform: "uppercase",
                                          whiteSpace: "nowrap", flexShrink: 0,
                                        }}
                                      >
                                        <Icon style={{ width: 8, height: 8 }} strokeWidth={2.5} />
                                        {label}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        ) : (
                          <em>Hours TBD</em>
                        )}
                      </div>
                      {/* Desktop-only meta suffix: hours already sit in
                          the right-side grid column, so on desktop we
                          only render the city here. */}
                      {e.kind !== "travel" && e.kind !== "off" && e.kind !== "repair" && e.city ? (
                        <div className="outreach-city-desktop" style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                          {e.city}
                        </div>
                      ) : null}
                    </div>
                    {/* Right side of the row — hours + kind badge + calendar
                        icon in a fixed-width CSS grid so hours line up in
                        their own column across every row regardless of
                        which badge (Outreach / Travel / Off / Repair)
                        sits beside them. On narrow screens the hours are
                        inlined above with the time text instead. */}
                    <div
                      className="outreach-entry-right"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "56px auto 15px",
                        alignItems: "center",
                        columnGap: 8,
                        flexShrink: 0,
                      }}
                    >
                      <div
                        className="outreach-hrs-desktop"
                        data-testid={`outreach-entry-hours-${e.date}`}
                        style={{
                          textAlign: "left",
                          whiteSpace: "nowrap",
                          fontSize: 13,
                          fontWeight: 700,
                          color: TEXT,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {rowHrs || ""}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "flex-start", gap: 4 }}>
                      {(!e.kind || (e.kind !== "travel" && e.kind !== "off" && e.kind !== "repair")) && (() => {
                        // Show unique per-window categories (excluding
                        // Work, per user request). If all windows are Work
                        // fall back to the single "Work" pill so days
                        // without any special category still get a badge.
                        const WT_LABEL = { outreach: "Work", admin: "Admin", driving: "Driving", repair: "Repair", other: "Other" };
                        const WT_COLOR = { outreach: DARK_GREEN, admin: "#8A5A00", driving: "#1F4E79", repair: "#C77D26", other: "#6B7A73" };
                        const WT_ICON  = { outreach: MapPin,     admin: Wrench,     driving: Plane,     repair: Wrench,   other: MapPin };
                        const uniq = [];
                        const seen = new Set();
                        for (const w of (e.windows || [])) {
                          const wt = (w.work_type || "outreach").toLowerCase();
                          if (wt === "outreach") continue; // skip Work
                          if (seen.has(wt)) continue;
                          seen.add(wt);
                          uniq.push(wt);
                        }
                        const chips = uniq.length
                          ? uniq
                          : ["outreach"]; // fallback: plain "Work" pill
                        return chips.map((wt) => {
                          const custom = workTypes.find((t) => t.key === wt);
                          const label = (custom?.label) || WT_LABEL[wt] || wt;
                          const color = WT_COLOR[wt] || "#6B7A73";
                          const Icon = WT_ICON[wt] || MapPin;
                          return (
                            <span key={wt} data-kind-badge data-testid={`outreach-row-cat-${wt}`} className="outreach-cat-right" style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              padding: "2px 6px", borderRadius: 999,
                              background: `${color}24`, color,
                              fontSize: 10, fontWeight: 700, letterSpacing: "0.05em",
                              textTransform: "uppercase",
                            }}>
                              <Icon style={{ width: 10, height: 10 }} strokeWidth={2.5} />
                              {label}
                            </span>
                          );
                        });
                      })()}
                      {/* No Travel / Repair / Admin badges — every row reads
                          like a Work row: total hours + per-slot task chips.
                          Off keeps its badge (an Off day has no slots, so the
                          badge is the row's only info). */}
                      {e.kind === "off" && (
                        <span data-kind-badge style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          padding: "2px 6px", borderRadius: 999,
                          background: "rgba(180,53,27,0.14)", color: "#B4351B",
                          fontSize: 10, fontWeight: 700, letterSpacing: "0.05em",
                          textTransform: "uppercase",
                        }}>
                          <CoffeeIcon style={{ width: 10, height: 10 }} strokeWidth={2.5} />
                          Off
                        </span>
                      )}
                      </div>
                      <CalendarDays style={{ width: 15, height: 15, color: MUTED, flexShrink: 0 }} />
                    </div>
                  </button>
                        );
                      })}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Manage cities dialog — add, rename or delete city options. */}
      <Dialog open={manageCitiesOpen} onOpenChange={(open) => { setManageCitiesOpen(open); if (!open) { setRenameCityFrom(""); setNewCity(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage cities</DialogTitle>
          </DialogHeader>
          <div style={{ display: "grid", gap: 8 }} data-testid="outreach-city-manage-dialog">
            {cities.length === 0 && (
              <p style={{ fontSize: 13, color: MUTED, margin: 0 }}>No cities yet — add one below.</p>
            )}
            {cities.map((c) => (
              <div key={c} style={{ display: "flex", alignItems: "center", gap: 6 }} data-testid={`outreach-city-row-${c}`}>
                {renameCityFrom === c ? (
                  <>
                    <input
                      autoFocus
                      value={renameCityTo}
                      onChange={(e) => setRenameCityTo(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") doRenameCity(); }}
                      data-testid="outreach-city-rename-input"
                      style={{
                        flex: 1, height: 38, borderRadius: 8, padding: "0 10px",
                        fontSize: 13, background: "#FFFFFF", border: `1px solid ${BORDER}`,
                        outline: "none", color: TEXT, fontFamily: "inherit", minWidth: 0,
                      }}
                    />
                    <Button
                      onClick={doRenameCity}
                      disabled={!renameCityTo.trim() || renameCityTo.trim() === c}
                      data-testid="outreach-city-rename-save"
                      style={{ background: DARK_GREEN, color: "white", height: 34, padding: "0 12px" }}
                    >
                      Save
                    </Button>
                    <Button variant="outline" onClick={() => setRenameCityFrom("")} style={{ height: 34, padding: "0 12px" }}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <span style={{ flex: 1, fontSize: 13, color: TEXT, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c}</span>
                    <button
                      type="button"
                      onClick={() => openRenameCity(c)}
                      data-testid={`outreach-city-edit-btn-${c}`}
                      title={`Rename "${c}"`}
                      style={{
                        background: "transparent", border: `1px solid ${BORDER}`,
                        borderRadius: 8, padding: 7, cursor: "pointer",
                        color: LABEL_GREEN, display: "flex", alignItems: "center", flexShrink: 0,
                      }}
                    >
                      <Pencil style={{ width: 13, height: 13 }} />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeCity(c)}
                      data-testid={`outreach-city-delete-btn-${c}`}
                      title={`Delete "${c}"`}
                      style={{
                        background: "transparent", border: `1px solid ${BORDER}`,
                        borderRadius: 8, padding: 7, cursor: "pointer",
                        color: "#B4351B", display: "flex", alignItems: "center", flexShrink: 0,
                      }}
                    >
                      <Trash2 style={{ width: 13, height: 13 }} />
                    </button>
                  </>
                )}
              </div>
            ))}
            <div style={{ display: "flex", gap: 6, paddingTop: 8, borderTop: `1px dashed ${BORDER}` }}>
              <input
                value={newCity}
                onChange={(e) => setNewCity(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addCity(); }}
                placeholder="Add a city"
                data-testid="outreach-city-new-input"
                style={{
                  flex: 1, height: 38, borderRadius: 8, padding: "0 10px",
                  fontSize: 13, background: "#FFFFFF", border: `1px solid ${BORDER}`,
                  outline: "none", color: TEXT, fontFamily: "inherit", minWidth: 0,
                }}
              />
              <Button
                onClick={addCity}
                disabled={!newCity.trim()}
                data-testid="outreach-city-new-save"
                style={{ background: DARK_GREEN, color: "white", height: 38, padding: "0 14px", flexShrink: 0 }}
              >
                <Plus style={{ width: 14, height: 14, marginRight: 4 }} /> Add
              </Button>
            </div>
            <p style={{ fontSize: 11, color: MUTED, margin: 0 }}>
              Renaming a city updates every outreach entry that uses it.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        @media (max-width: 640px) {
          .outreach-editor-city { flex-wrap: wrap !important; }
          .outreach-editor-city > button { flex: 0 0 auto; }
        }
        /* Entries-list layout:
           - Desktop (≥481px): hours live in the right-side grid column,
             city on its own second-meta line, all windows join with " · ".
           - Mobile (≤480px):  "hours · city" appears above the times,
             each window sits on its own line, and the right-side grid
             collapses to just "badge + calendar icon". */
        .outreach-meta-mobile { display: none; }
        /* Stacked time-slots-with-inline-category is now the default at
           every breakpoint. The joined single-line "· "-separated version
           and the right-column category chips are hidden globally so each
           category appears exactly once, right next to its time slot. */
        .outreach-times-mobile-list { display: block; }
        .outreach-times-desktop { display: none !important; }
        .outreach-cat-right { display: none !important; }
        @media (max-width: 480px) {
          .outreach-meta-mobile { display: block; }
          .outreach-city-desktop { display: none !important; }
          /* Hours now show on every viewport, right next to the calendar
             icon. The middle 92px "kind badge" column collapses to auto
             so the total sits flush against the icon. */
          .outreach-entry-right { grid-template-columns: auto auto 15px !important; }
        }
      `}</style>

      {/* Share schedule dialog (PNG export) */}
      <SchedulePreviewDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        defaultStaff={staff}
        defaultMonth={`${viewMonth.getFullYear()}-${String(viewMonth.getMonth() + 1).padStart(2, "0")}`}
        workTypes={workTypes}
        staffList={staffList}
        lockStaff={lockStaff}
      />

    </div>
  );
}

// View-only display card shown to viewers who don't have `outreach_edit`.
function ReadOnlyEntryCard({ entry }) {
  const DARK_GREEN = "#2E7D5B";
  const BORDER = "#E5E2DC";
  const MUTED = "#6B7A73";
  const TEXT = "#1C2B26";
  if (!entry) {
    return (
      <div style={{
        padding: "16px 14px",
        borderRadius: 10,
        border: `1.5px dashed ${BORDER}`,
        background: "#F9F8F6",
        color: MUTED,
        fontSize: 13,
        textAlign: "center",
        marginBottom: 18,
      }}>
        No entry for this day.
      </div>
    );
  }
  const kind = entry.kind || "outreach";
  const kindColor = kind === "travel" ? "#1F4E79" : kind === "off" ? "#B4351B" : kind === "repair" ? "#C77D26" : DARK_GREEN;
  const kindLabel = kind === "travel" ? "Travel" : kind === "off" ? "Off" : kind === "repair" ? "Repair" : "Work";
  const times = (entry.windows && entry.windows.length)
    ? entry.windows.map((w) => `${prettyTime(w.start_time)} – ${prettyTime(w.end_time)}`).join(" · ")
    : null;
  // Aggregate hours by work_type so we can render a small chip row per
  // entry (e.g. "Outreach 4h · Admin 1h"). Legacy windows without a
  // work_type default to outreach.
  const WT_LABEL = { outreach: "Work", admin: "Admin", driving: "Driving", other: "Other" };
  const WT_COLOR = { outreach: DARK_GREEN, admin: "#8A5A00", driving: "#1F4E79", other: "#6B7A73" };
  const workTypeMins = {};
  for (const w of (entry.windows || [])) {
    const wt = (w.work_type || "outreach").toLowerCase();
    try {
      const [sh, sm] = String(w.start_time).split(":").map(Number);
      let [eh, em] = String(w.end_time).split(":").map(Number);
      if (eh === 0 && em === 0 && w.end_time === "00:00") eh = 24;
      const mins = Math.max(0, (eh * 60 + em) - (sh * 60 + sm));
      workTypeMins[wt] = (workTypeMins[wt] || 0) + mins;
    } catch (_e) { /* ignore malformed */ }
  }
  const wtChips = Object.entries(workTypeMins).filter(([, m]) => m > 0);
  return (
    <div style={{
      padding: "14px 14px",
      borderRadius: 10,
      border: `1px solid ${BORDER}`,
      background: "white",
      marginBottom: 18,
    }}>
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "3px 8px", borderRadius: 999,
        background: `${kindColor}22`, color: kindColor,
        fontSize: 11, fontWeight: 700, letterSpacing: "0.05em",
        textTransform: "uppercase", marginBottom: 8,
      }}>
        {kindLabel}
      </div>
      {kind === "outreach" && (
        <div style={{ display: "flex", gap: 6, alignItems: "baseline", marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: MUTED, textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700 }}>City</span>
          <span style={{ fontSize: 14, color: TEXT }}>{entry.city || "—"}</span>
        </div>
      )}
      <div style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
        <span style={{ fontSize: 11, color: MUTED, textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700 }}>Hours</span>
        <span style={{ fontSize: 14, color: times ? TEXT : "#B4351B", fontStyle: times ? "normal" : "italic" }}>
          {times || (kind === "off" ? "All day" : kind === "repair" ? "Vehicle in maintenance" : "TBD")}
        </span>
      </div>
      {/* Work-type breakdown chips — only meaningful on outreach days
          where the user chose per-window types. */}
      {kind === "outreach" && wtChips.length > 0 && (wtChips.length > 1 || wtChips[0][0] !== "outreach") && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
          {wtChips.map(([wt, mins]) => (
            <span
              key={wt}
              data-testid={`outreach-entry-chip-${wt}`}
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                padding: "3px 8px", borderRadius: 999,
                background: `${WT_COLOR[wt] || "#6B7A73"}18`,
                color: WT_COLOR[wt] || "#6B7A73",
                fontSize: 11, fontWeight: 700,
              }}
            >
              {WT_LABEL[wt] || wt} · {(mins / 60).toFixed(mins % 60 === 0 ? 0 : 1)}h
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
